import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import Plot from "react-plotly.js";
import type * as Plotly from "plotly.js";
import { Button } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { ApiGraphData } from "./SimulationResults";
import { msToSeconds } from "../utils/time";
import { areSeriesEqual, insertInputPoint, moveInputPoint, removeInputPoint, roundToRange } from "./inputEditing";
import { describeTransform, IDENTITY_TRANSFORM, isIdentityTransform, SignalTransform } from "./signalTransform";
import { FocusRequest, GraphHighlight } from "./verification";


export type Series = { x: number[], y: number[], label: string };

interface StepChunk {
  step: number;
  start: number;
  end: number;
  passed: boolean | null; // null when no step result sample falls in the chunk
}

export const STEP_RESULT_LABEL = 'cppmodel.stepresult';
export const STEP_NUMBER_LABEL = 'cppmodel.stepnumber';
// Above this many chunks the step labels would overlap, so only hover remains.
const MAX_STEP_LABELS = 40;
// How close (in pixels) the pointer must be to grab a point of an edited input.
const POINT_HIT_RADIUS = 8;
const EDIT_MARKER_SIZE = 8;
// A click that moves the pointer further than this (in pixels) was a pan.
const CLICK_SLOP = 4;
// Plotly's default: a second click within this many ms makes a double-click.
const DOUBLE_CLICK_DELAY = 300;
// Wheel scaling of an adjusted signal: one notch (deltaY 100) scales by about 1.4.
const WHEEL_SCALE_RATE = 300;
// The axes stay still until the wheel has rested this long (ms).
const WHEEL_REST_DELAY = 500;
const ADJUSTED_LINE_WIDTH = 3.5;
// A shifted signal's edge snaps to another edge or marker this close (px).
const SNAP_DISTANCE = 8;

const PASS_COLOR = "rgba(42, 130, 83, 0.45)";
const FAIL_CHUNK_COLOR = "rgba(179, 68, 73, 0.22)";
const FAIL_COLOR = "rgba(179, 68, 73, 0.75)";
const UNKNOWN_COLOR = "rgba(101, 115, 108, 0.18)";
// Verification violations over the main plot, and requirement coverage.
const VIOLATION_FILL = "rgba(179, 68, 73, 0.12)";
const VIOLATION_LINE = "rgba(179, 68, 73, 0.7)";
const COVERAGE_FILL = "rgba(18, 31, 25, 0.06)";
const COVERAGE_LINE = "rgba(18, 31, 25, 0.45)";
// Vertical extent of the main plot, in paper units.
const MAIN_PLOT_DOMAIN: [number, number] = [0.2, 1];

export const findSeries = (series: Series[] | undefined, label: string) =>
  series?.find((item) => item.label.toLowerCase().includes(label));

// Split the timeline into chunks of consecutive samples with the same step
// number and give each chunk a verdict from the step results inside it.
const buildStepChunks = (stepSeries: Series, stepResult: Series | undefined): StepChunk[] => {
  const chunks: StepChunk[] = [];

  stepSeries.y.forEach((step, index) => {
    const time = stepSeries.x[index];
    const last = chunks[chunks.length - 1];

    if (last && last.step === step) {
      return;
    }

    if (last) {
      last.end = time;
    }

    chunks.push({ step, start: time, end: time, passed: null });
  });

  if (chunks.length > 0) {
    chunks[chunks.length - 1].end = stepSeries.x[stepSeries.x.length - 1];
  }

  // The step result may only be sampled when it changes, so it holds its last
  // value: a chunk sees the value in effect at its start plus every sample
  // inside it.
  if (stepResult) {
    chunks.forEach((chunk, chunkIndex) => {
      const isLast = chunkIndex === chunks.length - 1;
      let heldValue: number | null = null;

      stepResult.x.forEach((time, index) => {
        const value = stepResult.y[index];

        if (time <= chunk.start) {
          heldValue = value;
        } else if (isLast ? time <= chunk.end : time < chunk.end) {
          chunk.passed = (chunk.passed ?? true) && Boolean(value);
        }
      });

      if (heldValue !== null) {
        chunk.passed = (chunk.passed ?? true) && Boolean(heldValue);
      }
    });
  }

  return chunks;
};

// Measurement markers A and B, as times in seconds; null when not placed.
type MarkerId = "a" | "b";
type Markers = Record<MarkerId, number | null>;
const MARKER_IDS: MarkerId[] = ["a", "b"];
const NO_MARKERS: Markers = { a: null, b: null };

// Place the first missing marker, or move the one nearer to the time.
const placeMarker = (markers: Markers, time: number): Markers => {
  if (markers.a === null) {
    return { ...markers, a: time };
  }

  if (markers.b === null) {
    return { ...markers, b: time };
  }

  return Math.abs(time - markers.a) <= Math.abs(time - markers.b)
    ? { ...markers, a: time }
    : { ...markers, b: time };
};

// Value of a change-compressed series at a time: the value of its last point
// at or before that time, or null before its first point.
export const valueAt = (series: Series, time: number): number | null => {
  let low = 0;
  let high = series.x.length - 1;
  let found = -1;

  while (low <= high) {
    const middle = (low + high) >> 1;

    if (series.x[middle] <= time) {
      found = middle;
      low = middle + 1;
    } else {
      high = middle - 1;
    }
  }

  return found >= 0 ? series.y[found] : null;
};

// Index of the first sorted value at or after the given one.
const lowerBound = (values: number[], value: number) => {
  let low = 0;
  let high = values.length;

  while (low < high) {
    const middle = (low + high) >> 1;

    if (values[middle] < value) {
      low = middle + 1;
    } else {
      high = middle;
    }
  }

  return low;
};

const formatNumber = (value: number | null) => value === null ? "–" : `${Number(value.toPrecision(6))}`;

const describeChunk = (chunk: StepChunk) => {
  const verdict = chunk.passed === null ? 'no result' : chunk.passed ? 'passed' : 'failed';
  return `Step ${chunk.step}: ${verdict}<br>t = ${chunk.start} – ${chunk.end} s`;
};

// Timestamps arrive in milliseconds; the graph works in seconds throughout.
const toSecondsTimeline = (data: ApiGraphData): ApiGraphData => {
  const scale = (series: Series) => ({ ...series, x: series.x.map(msToSeconds) });
  return { ...data, inputs: data.inputs.map(scale), results: data.results.map(scale) };
};

// Categorical signal palette (blue, orange, aqua, violet, yellow, magenta).
// The order keeps neighbouring colours distinguishable with colour vision
// deficiencies (worst protan/deutan OKLab ΔE 9.2 on white). Green and red are
// left out because they mean pass/fail in the test strip.
const SIGNAL_COLORS = ["#2a78d6", "#eb6834", "#1baf7a", "#4a3aa7", "#eda100", "#e87ba4"];
// Past six signals the colours repeat with a different dash, so no two signals
// share the same look.
const SIGNAL_DASHES: Plotly.Dash[] = ["solid", "dash", "dot"];
const SIGNAL_LINE_WIDTH = 2;

export const GRID_COLOR = "#edf1ef";
export const ZERO_LINE_COLOR = "#cdd6d1";
export const AXIS_TEXT_COLOR = "#65736c"; // --brand-muted

// The I/O signals by their legend name, with timestamps as delivered (ms).
export const getSignalSeries = (data: ApiGraphData): Map<string, Series> => new Map([
  ...data.inputs.map((input) => [`In: ${input.label}`, input] as const),
  ...data.results.filter((result) => !result.label.includes('CppModel.')).map((result) => [`Out: ${result.label}`, result] as const),
]);

export interface LegendItem {
  name: string;
  color: string;
  dash: Plotly.Dash;
}

// The signals the graph draws as named lines, with their line style, so the
// legend can live outside the plot. Styles follow the signal's position among
// all signals (inputs first), so hiding one never restyles the others. With a
// previous run to compare with, its signals missing from data come last.
export const getLegendItems = (data: ApiGraphData, previousData?: ApiGraphData): LegendItem[] => {
  const names = Array.from(new Set([
    ...Array.from(getSignalSeries(data).keys()),
    ...(previousData ? Array.from(getSignalSeries(previousData).keys()) : []),
  ]));

  return names.map((name, index) => ({ name, ...getSignalStyle(index) }));
};

// Line style of the index-th series: colours in palette order, then repeated
// with the next dash.
export const getSignalStyle = (index: number): Omit<LegendItem, "name"> => ({
  color: SIGNAL_COLORS[index % SIGNAL_COLORS.length],
  dash: SIGNAL_DASHES[Math.floor(index / SIGNAL_COLORS.length) % SIGNAL_DASHES.length],
});

// Plotly only resizes on window resize, so forward size changes of the
// wrapper (pane splitter, layout changes) as window resize events.
export const usePlotResize = (wrapperRef: React.RefObject<HTMLElement>) => {
  React.useEffect(() => {
    const wrapper = wrapperRef.current;

    if (!wrapper) {
      return;
    }

    let animationFrame: number | null = null;

    const resizePlot = () => {
      if (animationFrame !== null) {
        window.cancelAnimationFrame(animationFrame);
      }

      animationFrame = window.requestAnimationFrame(() => {
        window.dispatchEvent(new Event("resize"));
      });
    };

    const resizeObserver = new ResizeObserver(resizePlot);
    resizeObserver.observe(wrapper);
    resizePlot();

    return () => {
      resizeObserver.disconnect();

      if (animationFrame !== null) {
        window.cancelAnimationFrame(animationFrame);
      }
    };
  }, [wrapperRef]);
};

interface SimulationGraphProps {
  title?: string;
  data: ApiGraphData;
  // Signals explicitly hidden by the user; missing names are visible.
  traceVisibility: Record<string, boolean>;
  // The input series whose points can be dragged, added and removed.
  editingInput?: string | null;
  onInputChange?: (series: Series) => void;
  // The inputs the shown results were produced with. An input that has been
  // edited since is drawn faintly underneath its edited version.
  referenceInputs?: Series[];
  // How signals are shifted and scaled, by legend name.
  transforms?: Record<string, SignalTransform>;
  // The signal shifted by dragging and scaled by the wheel on the plot.
  adjustingSignal?: string | null;
  onTransformChange?: (name: string, transform: SignalTransform) => void;
  onAdjustEnd?: () => void;
  // Time intervals drawn over the main plot (verification, requirements).
  highlights?: GraphHighlight[];
  // Zoom to this range whenever a new request arrives.
  focusRequest?: FocusRequest | null;
  // An earlier run drawn faintly under data, signal by signal, to compare with.
  previousData?: ApiGraphData;
}

// Name of the line showing a signal's value in the previous run.
export const previousTraceName = (traceName: string) => `${traceName} (previous)`;

interface PlotAxis {
  _offset: number;
  _length: number;
  range: [number, number];
  l2p: (value: number) => number;
  p2l: (pixel: number) => number;
}

const SimulationGraph: React.FC<SimulationGraphProps> = ({
  title = "Plot",
  data: dataMs,
  traceVisibility,
  editingInput = null,
  onInputChange,
  referenceInputs,
  transforms,
  adjustingSignal = null,
  onTransformChange,
  onAdjustEnd,
  highlights,
  focusRequest,
  previousData: previousDataMs,
}) => {
  const data = useMemo(() => toSecondsTimeline(dataMs), [dataMs]);
  const previousData = useMemo(() => previousDataMs && toSecondsTimeline(previousDataMs), [previousDataMs]);
  const [zoomRange, setZoomRange] = useState<[number, number] | null>(null);
  const [plotData, setPlotData] = useState([] as Partial<Plotly.ScatterData>[]);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const graphDivRef = useRef<HTMLElement | null>(null);
  const cursorLineRef = useRef<HTMLDivElement>(null);
  const cursorLabelRef = useRef<HTMLDivElement>(null);
  const snapGuideRef = useRef<HTMLDivElement>(null);
  // Axis ranges held still while a point is dragged, so the plot doesn't
  // rescale under the pointer.
  const [frozenRanges, setFrozenRanges] = useState<{ x: [number, number]; y: [number, number] } | null>(null);
  const editingSeries = editingInput && onInputChange
    ? dataMs.inputs.find((input) => input.label === editingInput)
    : undefined;
  const editingSeriesRef = useRef(editingSeries);
  editingSeriesRef.current = editingSeries;
  const adjustingRef = useRef<string | null>(null);
  adjustingRef.current = adjustingSignal && onTransformChange ? adjustingSignal : null;
  const transformsRef = useRef(transforms);
  transformsRef.current = transforms;
  const [markers, setMarkers] = useState<Markers>(NO_MARKERS);
  const markersRef = useRef(markers);
  markersRef.current = markers;
  const markerElementsRef = useRef<Record<MarkerId, HTMLDivElement | null>>({ a: null, b: null });
  const draggingMarkerRef = useRef<MarkerId | null>(null);
  const pointerDownRef = useRef<{ x: number; y: number } | null>(null);
  // The markers before the last placing click, restored if that click turns
  // out to be the first half of a double-click.
  const lastClickRef = useRef<{ time: number; previous: Markers } | null>(null);

  // console.log("Simulation Graph Data:", data);

  const stepChunks = useMemo(() => {
    const stepSeries = findSeries(data.results, STEP_NUMBER_LABEL);

    if (!stepSeries || stepSeries.x.length === 0) {
      return [];
    }

    return buildStepChunks(stepSeries, findSeries(data.results, STEP_RESULT_LABEL));
  }, [data]);

  // Chunk backgrounds in the verdict strip plus dotted step boundaries across
  // the main plot.
  const stepShapes = useMemo<Partial<Plotly.Shape>[]>(() => {
    // Above the grid lines but still below the failure trace. Supported by
    // plotly.js, missing from its type definitions.
    const layer = "between" as Plotly.Shape["layer"];

    const chunkShapes = stepChunks.map<Partial<Plotly.Shape>>((chunk) => ({
      type: "rect",
      xref: "x",
      yref: "y2",
      x0: chunk.start,
      x1: chunk.end,
      y0: 0,
      y1: 1,
      fillcolor: chunk.passed === null ? UNKNOWN_COLOR : chunk.passed ? PASS_COLOR : FAIL_CHUNK_COLOR,
      line: { width: 0 },
      layer,
    }));

    // Boundaries come after all chunks so no chunk covers them in the strip.
    const boundaryShapes = stepChunks.slice(1).map<Partial<Plotly.Shape>>((chunk) => ({
      type: "line",
      xref: "x",
      yref: "paper",
      x0: chunk.start,
      x1: chunk.start,
      y0: 0,
      y1: 1,
      line: { color: "rgba(18, 31, 25, 0.25)", width: 1, dash: "dot" },
      layer,
    }));

    return [...chunkShapes, ...boundaryShapes];
  }, [stepChunks]);

  // Highlighted intervals across the main plot, labelled at the top. An
  // interval of one instant still shows as its border line.
  const highlightShapes = useMemo<Partial<Plotly.Shape>[]>(() => {
    if (!highlights || highlights.length === 0) {
      return [];
    }

    const layer = "between" as Plotly.Shape["layer"];
    const runEnd = Math.max(0, ...[...data.inputs, ...data.results].flatMap((series) => series.x));

    return highlights.flatMap((highlight) => highlight.intervals.map<Partial<Plotly.Shape>>(([from, to], index) => {
      const isViolation = highlight.kind === "violation";

      return {
        type: "rect",
        xref: "x",
        yref: "paper",
        x0: from ?? 0,
        x1: to ?? runEnd,
        y0: MAIN_PLOT_DOMAIN[0],
        y1: MAIN_PLOT_DOMAIN[1],
        fillcolor: isViolation ? VIOLATION_FILL : COVERAGE_FILL,
        line: { color: isViolation ? VIOLATION_LINE : COVERAGE_LINE, width: 1, dash: isViolation ? "solid" : "dash" },
        layer,
        // Only the first interval of each highlight is named, to keep it calm.
        ...(index === 0 ? {
          label: {
            text: highlight.label,
            textposition: "top left",
            font: { size: 10, color: isViolation ? VIOLATION_LINE : COVERAGE_LINE },
            padding: 4,
          },
        } : {}),
      } as Partial<Plotly.Shape>;
    }));
  }, [data, highlights]);

  const stepAnnotations = useMemo<Partial<Plotly.Annotations>[]>(() => {
    if (stepChunks.length > MAX_STEP_LABELS) {
      return [];
    }

    return stepChunks.map((chunk) => ({
      xref: "x",
      yref: "y2",
      x: (chunk.start + chunk.end) / 2,
      y: 0.5,
      text: `${chunk.step}`,
      showarrow: false,
      font: { size: 10, color: "#121f19" },
    }));
  }, [stepChunks]);

  const loadGraphData = React.useCallback(() => {
    // Convert into Plotly.js format
    let graphData: Partial<Plotly.ScatterData>[] = [];
    let verificationData: Partial<Plotly.ScatterData>[] = [];

    const getTraceVisibility = (traceName: string): boolean => {
      return traceVisibility[traceName] !== false;
    };

    const signalStyles = new Map(getLegendItems(data, previousData).map((item) => [item.name, item]));
    const getSignalLine = (traceName: string): Partial<Plotly.ScatterLine> => {
      const style = signalStyles.get(traceName);
      const width = traceName === adjustingSignal ? ADJUSTED_LINE_WIDTH : SIGNAL_LINE_WIDTH;
      return { shape: "hv", width, color: style?.color, dash: style?.dash };
    };

    // The points of a signal (times in s) as drawn: shifted and scaled as set
    // in the legend, with the original point shown on hover.
    const getShownPoints = (traceName: string, series: Series, isEditing = false): Partial<Plotly.ScatterData> => {
      const transform = transforms?.[traceName];

      if (isEditing || !transform || isIdentityTransform(transform)) {
        return { x: series.x, y: series.y };
      }

      return {
        x: series.x.map((time) => Number((time + transform.timeShift).toPrecision(12))),
        y: series.y.map((value) => value * transform.scale + transform.offset),
        customdata: series.x.map((time, index) => [time, series.y[index]]),
        hovertemplate: `(%{x}, %{y})<br>original: (%{customdata[0]}, %{customdata[1]})<extra>${traceName}<br>${describeTransform(transform)}</extra>`,
      } as Partial<Plotly.ScatterData>;
    };

    if (data.inputs !== undefined) {
      data.inputs.forEach((input) => {
        const traceName = `In: ${input.label}`;
        const isEditing = input.label === editingInput && Boolean(onInputChange);
        const line = getSignalLine(traceName);
        const reference = referenceInputs?.find((item) => item.label === input.label);

        if (reference && !areSeriesEqual(reference, dataMs.inputs.find((item) => item.label === input.label))) {
          graphData.push({
            ...getShownPoints(traceName, { ...reference, x: reference.x.map(msToSeconds) }, isEditing),
            type: "scatter",
            mode: "lines",
            name: `${traceName} (last run)`,
            visible: isEditing || getTraceVisibility(traceName),
            line: { ...line, width: 1, dash: "dot" },
            opacity: 0.5,
            hoverinfo: "skip",
          });
        }

        graphData.push({
          ...getShownPoints(traceName, input, isEditing),
          type: "scatter",
          mode: isEditing ? "lines+markers" : "lines",
          name: traceName,
          visible: isEditing || getTraceVisibility(traceName),
          line,
          // Plotly fails on a marker key set to undefined, so only add it here.
          ...(isEditing ? { marker: { size: EDIT_MARKER_SIZE, color: line.color, line: { width: 1, color: "#ffffff" } } } : {}),
        });
      });
    }
    if (data.results !== undefined) {
      graphData.push(
        ...data.results?.map((result) => {
          if (!result.label.includes('CppModel.')) {
            const traceName = `Out: ${result.label}`;

            return ({
              ...getShownPoints(traceName, result),
              type: "scatter",
              mode: "lines",
              name: traceName,
              visible: getTraceVisibility(traceName),
              line: getSignalLine(traceName),
            })
          } else if (result.label.includes('CppModel.StepResult')) {
            const failure: Partial<Plotly.ScatterData> = 
              {
                x: result.x,
                y: result.y.map((v) => (v) ? 0 : 1), // Inverted binary signal
                showlegend: false,
                type: "scatter",
                mode: "lines",
                fill: "tozeroy",
                line: { shape: "hv", width: 0, color: "rgb(0, 0, 0)" }, // "hv" makes it step-like
                fillcolor: FAIL_COLOR,
                name: "Failure",
                xaxis: "x",
                yaxis: "y2",
                hoverinfo: "none", // Hide hover info for binary signal
              };

            // With step chunks the chunk backgrounds show success; only the
            // exact failing samples are drawn on top of them.
            if (stepChunks.length > 0) {
              return failure;
            }

            verificationData.push(failure);
            return (
              {
                x: result.x,
                y: result.y,
                showlegend: false, // Hide from legend
                type: "scatter",
                mode: "lines",
                fill: "tozeroy",
                line: { shape: "hv", width: 0, color: "rgb(0, 0, 0)" }, // "hv" makes it step-like
                fillcolor: PASS_COLOR,
                name: "Success",
                xaxis: "x",
                yaxis: "y2",
                hoverinfo: "none", // Hide hover info for binary signal
              }
            )
          }
          // else if (result.label.includes('CppModel.Step')) {
          //   // From result.y get only unique values
          //   const uniqueValues = new Set(result.y);
          //   const uniqueValuesText = Array.from(uniqueValues).map((v) => `${v}`);
          //   // From result.x get every first value for each unique value in result.y
          //   const uniqueX = result.x.filter((_, i) => result.y[i] !== result.y[i - 1]);
          //   // console.log("Unique X:", uniqueX);
          //   // console.log("Unique Y:", uniqueValues);
          //   verificationData.push(
          //     // Step number annotations
          //     {
          //       x: uniqueX,
          //       y: new Array(uniqueX.length).fill(1), // Position slightly above graph
          //       mode: "markers",
          //       marker: { size: 2, color: "rgb(73, 73, 73)" },
          //       // text: uniqueValuesText,
          //       textposition: "middle center",
          //       showlegend: false,
          //       xaxis: "x",
          //       yaxis: "y2",
          //       hoverinfo: "none", // Hide hover info for step numbers
          //     }
          //   );
          //   return ({});

          // }
          return {};
        }) as Partial<Plotly.ScatterData>[],
      );
    }
    // The previous run under the current one, in the same colours.
    if (previousData) {
      Array.from(getSignalSeries(previousData)).forEach(([traceName, series]) => {
        graphData.unshift({
          ...getShownPoints(traceName, series),
          type: "scatter",
          mode: "lines",
          name: previousTraceName(traceName),
          visible: getTraceVisibility(traceName),
          line: { ...getSignalLine(traceName), width: 1.5, dash: "dot" },
          opacity: 0.55,
        });
      });
    }

    graphData.push(...verificationData);

    // Invisible markers at chunk centres so hovering the strip names the step.
    if (stepChunks.length > 0) {
      graphData.push({
        x: stepChunks.map((chunk) => (chunk.start + chunk.end) / 2),
        y: stepChunks.map(() => 0.5),
        type: "scatter",
        mode: "markers",
        marker: { size: 14, opacity: 0 },
        text: stepChunks.map(describeChunk),
        hovertemplate: "%{text}<extra></extra>",
        showlegend: false,
        name: "Steps",
        xaxis: "x",
        yaxis: "y2",
      });
    }

    // console.log("Heatmap data:", heatmapData);

    setPlotData(graphData);
  }, [adjustingSignal, data, dataMs, editingInput, onInputChange, previousData, referenceInputs, stepChunks, traceVisibility, transforms]);

  // Load parameters when component is mounted
  React.useEffect(() => {
    loadGraphData();
  }
    , [data, loadGraphData]);

  usePlotResize(wrapperRef);

  useEffect(() => {
    if (focusRequest) {
      setZoomRange(focusRequest.range);
    }
  }, [focusRequest]);

  // Vertical cursor line spanning every subplot, including the test strip, so
  // outputs and step verdicts can be correlated at the same time. Updated
  // directly on the DOM to avoid re-rendering the plot on every mouse move.
  const hideCursorLine = () => {
    if (cursorLineRef.current) {
      cursorLineRef.current.style.display = "none";
    }
  };

  const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    if (editingSeriesRef.current) {
      setWrapperClass("is-over-point", findPointAt(event) >= 0);
    }

    const wrapper = wrapperRef.current;
    const graphDiv = graphDivRef.current as (HTMLElement & { _fullLayout?: any }) | null;
    const line = cursorLineRef.current;
    const label = cursorLabelRef.current;
    const plotSize = graphDiv?._fullLayout?._size;
    const xAxis = graphDiv?._fullLayout?.xaxis;

    if (!wrapper || !graphDiv || !line || !label || !plotSize || !xAxis) {
      return;
    }

    const wrapperRect = wrapper.getBoundingClientRect();
    const graphRect = graphDiv.getBoundingClientRect();
    const plotX = event.clientX - graphRect.left - plotSize.l;
    const plotY = event.clientY - graphRect.top - plotSize.t;

    if (plotX < 0 || plotX > plotSize.w || plotY < 0 || plotY > plotSize.h) {
      hideCursorLine();
      return;
    }

    const offsetLeft = graphRect.left - wrapperRect.left + plotSize.l;
    const offsetTop = graphRect.top - wrapperRect.top + plotSize.t;
    const time = xAxis.p2d(plotX);

    line.style.display = "block";
    line.style.left = `${offsetLeft + plotX}px`;
    line.style.top = `${offsetTop}px`;
    line.style.height = `${plotSize.h}px`;
    label.textContent = typeof time === "number" ? `${Number(time.toPrecision(6))} s` : `${time} s`;
  };

  const getPlotAxes = () => {
    const graphDiv = graphDivRef.current as (HTMLElement & { _fullLayout?: any }) | null;
    const xAxis = graphDiv?._fullLayout?.xaxis as PlotAxis | undefined;
    const yAxis = graphDiv?._fullLayout?.yaxis as PlotAxis | undefined;

    if (!graphDiv || !xAxis || !yAxis) {
      return null;
    }

    return { graphDiv, xAxis, yAxis };
  };

  // Pointer position in the main plot, in pixels and in data units (time in ms).
  const getPlotPosition = (event: { clientX: number; clientY: number }) => {
    const axes = getPlotAxes();

    if (!axes) {
      return null;
    }

    const { graphDiv, xAxis, yAxis } = axes;
    const rect = graphDiv.getBoundingClientRect();
    const px = event.clientX - rect.left - xAxis._offset;
    const py = event.clientY - rect.top - yAxis._offset;

    return {
      px,
      py,
      isInside: px >= 0 && px <= xAxis._length && py >= 0 && py <= yAxis._length,
      time: xAxis.p2l(px) * 1000,
      value: roundToRange(yAxis.p2l(py), Math.abs(yAxis.range[1] - yAxis.range[0])),
      xAxis,
      yAxis,
    };
  };

  // Index of the edited input's point under the pointer, or -1.
  const findPointAt = (event: { clientX: number; clientY: number }): number => {
    const series = editingSeriesRef.current;
    const position = getPlotPosition(event);

    if (!series || !position) {
      return -1;
    }

    let nearestIndex = -1;
    let nearestDistance = POINT_HIT_RADIUS;

    series.x.forEach((time, index) => {
      const distance = Math.hypot(
        position.xAxis.l2p(msToSeconds(time)) - position.px,
        position.yAxis.l2p(series.y[index]) - position.py,
      );

      if (distance <= nearestDistance) {
        nearestDistance = distance;
        nearestIndex = index;
      }
    });

    return nearestIndex;
  };

  const setWrapperClass = (className: string, isOn: boolean) => {
    wrapperRef.current?.classList.toggle(className, isOn);
  };

  // Grab a point of the edited input before Plotly starts panning, and move it
  // with the pointer until release.
  const handleMouseDownCapture = (event: React.MouseEvent<HTMLDivElement>) => {
    pointerDownRef.current = { x: event.clientX, y: event.clientY };

    if (adjustingRef.current && event.button === 0 && !isOnOverlay(event.target)) {
      startSignalDrag(event);
      return;
    }

    if (!editingSeriesRef.current || event.button !== 0 || isOnOverlay(event.target)) {
      return;
    }

    const pointIndex = findPointAt(event);
    const axes = getPlotAxes();

    if (pointIndex < 0 || !axes) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    setFrozenRanges({ x: [...axes.xAxis.range], y: [...axes.yAxis.range] });
    setWrapperClass("is-dragging-point", true);

    let animationFrame: number | null = null;

    const handleMove = (moveEvent: MouseEvent) => {
      if (animationFrame !== null) {
        window.cancelAnimationFrame(animationFrame);
      }

      animationFrame = window.requestAnimationFrame(() => {
        animationFrame = null;
        const series = editingSeriesRef.current;
        const position = getPlotPosition(moveEvent);

        if (series && position && onInputChange) {
          onInputChange(moveInputPoint(series, pointIndex, position.time, position.value));
        }
      });
    };

    const handleUp = () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
      setWrapperClass("is-dragging-point", false);
      setFrozenRanges(null);
    };

    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
  };

  // Drag the adjusted signal instead of panning: sideways shifts it in time,
  // up and down offsets it. Shift locks the drag to its main direction.
  // Edges (change times, s) of each shown signal as drawn, i.e. with its
  // time shift: what dragged signals and markers stick to.
  const signalEdges = useMemo(() => new Map(
    Array.from(getSignalSeries(dataMs))
      .filter(([name]) => traceVisibility[name] !== false)
      .map(([name, series]) => {
        const shift = transforms?.[name]?.timeShift ?? 0;
        return [name, series.x.map((time) => Number((msToSeconds(time) + shift).toPrecision(12)))] as const;
      }),
  ), [dataMs, traceVisibility, transforms]);
  const allSignalEdges = useMemo(
    () => Array.from(signalEdges.values()).flat().sort((a, b) => a - b),
    [signalEdges],
  );

  // A vertical line where something has snapped; null hides it.
  const showSnapGuide = (time: number | null) => {
    const guide = snapGuideRef.current;
    const geometry = getTimeAxisGeometry();

    if (!guide) {
      return;
    }

    if (time === null || !geometry) {
      guide.style.display = "none";
      return;
    }

    guide.style.display = "block";
    guide.style.left = `${geometry.left + (time - geometry.range[0]) / (geometry.range[1] - geometry.range[0]) * geometry.width}px`;
    guide.style.top = `${geometry.top}px`;
    guide.style.height = `${geometry.height}px`;
  };

  // The signal edge nearest to a time, if it is within snapping reach.
  const snapToEdge = (time: number): number | null => {
    const geometry = getTimeAxisGeometry();

    if (!geometry) {
      return null;
    }

    const tolerance = SNAP_DISTANCE * Math.abs(geometry.range[1] - geometry.range[0]) / geometry.width;
    const index = lowerBound(allSignalEdges, time);
    const nearest = [allSignalEdges[index - 1], allSignalEdges[index]]
      .filter((edge): edge is number => edge !== undefined)
      .sort((a, b) => Math.abs(a - time) - Math.abs(b - time))[0];

    return nearest !== undefined && Math.abs(nearest - time) <= tolerance ? nearest : null;
  };

  const startSignalDrag = (event: React.MouseEvent<HTMLDivElement>) => {
    const name = adjustingRef.current;
    const position = getPlotPosition(event);

    if (!name || !position?.isInside || !onTransformChange) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const xRange: [number, number] = [...position.xAxis.range];
    const yRange: [number, number] = [...position.yAxis.range];
    const xSpan = Math.abs(xRange[1] - xRange[0]);
    const ySpan = Math.abs(yRange[1] - yRange[0]);
    const secondsPerPixel = (xRange[1] - xRange[0]) / position.xAxis._length;
    const valuePerPixel = (yRange[1] - yRange[0]) / position.yAxis._length;
    const start = transformsRef.current?.[name] ?? IDENTITY_TRANSFORM;
    const snapTolerance = SNAP_DISTANCE * Math.abs(secondsPerPixel);

    // Edges (change times, s) of the dragged signal, and where they can snap
    // to: the edges of the other shown signals as drawn, and the markers.
    const ownEdges = (getSignalSeries(dataMs).get(name)?.x ?? []).map(msToSeconds);
    const snapTargets = [
      ...Array.from(signalEdges)
        .filter(([other]) => other !== name)
        .flatMap(([, edges]) => edges),
      ...MARKER_IDS.map((id) => markersRef.current[id]).filter((time): time is number => time !== null),
    ].sort((a, b) => a - b);

    // The shift that lines up the edge nearest to another edge, if one is
    // within reach.
    const snapShift = (shift: number): { shift: number; time: number } | null => {
      let best: { shift: number; time: number; distance: number } | null = null;
      const shownMin = Math.min(...xRange) - snapTolerance;
      const shownMax = Math.max(...xRange) + snapTolerance;

      ownEdges.forEach((edge) => {
        const shown = edge + shift;

        if (shown < shownMin || shown > shownMax) {
          return;
        }

        const index = lowerBound(snapTargets, shown);

        [snapTargets[index - 1], snapTargets[index]].forEach((target) => {
          const distance = target === undefined ? Infinity : Math.abs(target - shown);

          if (distance <= snapTolerance && (!best || distance < best.distance)) {
            best = { shift: Number((target - edge).toPrecision(12)), time: target, distance };
          }
        });
      });

      return best;
    };

    setFrozenRanges({ x: xRange, y: yRange });
    setWrapperClass("is-dragging-signal", true);

    let animationFrame: number | null = null;
    let pendingMove: MouseEvent | null = null;

    const applyMove = (moveEvent: MouseEvent) => {
      let dx = moveEvent.clientX - event.clientX;
      let dy = moveEvent.clientY - event.clientY;

      if (moveEvent.shiftKey) {
        if (Math.abs(dx) > Math.abs(dy)) {
          dy = 0;
        } else {
          dx = 0;
        }
      }

      // Edges stick to other edges and markers; Alt moves freely.
      const freeShift = start.timeShift + dx * secondsPerPixel;
      const snap = dx !== 0 && !moveEvent.altKey ? snapShift(freeShift) : null;

      showSnapGuide(snap?.time ?? null);
      onTransformChange(name, {
        ...start,
        timeShift: snap ? snap.shift : roundToRange(freeShift, xSpan),
        offset: roundToRange(start.offset - dy * valuePerPixel, ySpan),
      });
    };

    // Moves are applied once per frame; the last one is applied on release
    // even if its frame has not come yet.
    const handleMove = (moveEvent: MouseEvent) => {
      pendingMove = moveEvent;

      if (animationFrame === null) {
        animationFrame = window.requestAnimationFrame(() => {
          animationFrame = null;

          if (pendingMove) {
            applyMove(pendingMove);
            pendingMove = null;
          }
        });
      }
    };

    const handleUp = () => {
      if (animationFrame !== null) {
        window.cancelAnimationFrame(animationFrame);
        animationFrame = null;
      }

      if (pendingMove) {
        applyMove(pendingMove);
        pendingMove = null;
      }

      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
      showSnapGuide(null);
      setWrapperClass("is-dragging-signal", false);
      setFrozenRanges(null);
    };

    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
  };

  // The wheel over the plot scales the adjusted signal about the value under
  // the pointer, so that part of the signal stays put. Over an axis the wheel
  // still zooms. Successive wheel events build on each other before the
  // parent re-renders, so the latest transform is kept here.
  const wheelTransformRef = useRef<{ name: string; transform: SignalTransform } | null>(null);
  const wheelRestTimerRef = useRef<number | null>(null);
  const handleWheelRef = useRef<(event: WheelEvent) => void>(() => undefined);
  handleWheelRef.current = (event: WheelEvent) => {
    const name = adjustingRef.current;
    const position = getPlotPosition(event);
    const delta = event.deltaY || event.deltaX;

    if (!name || !onTransformChange || !position?.isInside || isOnOverlay(event.target) || delta === 0) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const pending = wheelTransformRef.current;
    const start = pending?.name === name ? pending.transform : transformsRef.current?.[name] ?? IDENTITY_TRANSFORM;

    if (start.scale === 0) {
      return;
    }

    const factor = Math.exp(-Math.max(Math.min(delta, 100), -100) / WHEEL_SCALE_RATE);
    const scale = Number((start.scale * factor).toPrecision(6));
    const pivot = position.yAxis.p2l(position.py);
    const ySpan = Math.abs(position.yAxis.range[1] - position.yAxis.range[0]);
    const next = {
      ...start,
      scale,
      offset: roundToRange(pivot - (pivot - start.offset) * (scale / start.scale), ySpan),
    };

    wheelTransformRef.current = { name, transform: next };
    onTransformChange(name, next);

    if (wheelRestTimerRef.current === null) {
      setFrozenRanges({ x: [...position.xAxis.range], y: [...position.yAxis.range] });
    } else {
      window.clearTimeout(wheelRestTimerRef.current);
    }

    wheelRestTimerRef.current = window.setTimeout(() => {
      wheelRestTimerRef.current = null;
      wheelTransformRef.current = null;
      setFrozenRanges(null);
    }, WHEEL_REST_DELAY);
  };

  // React's wheel listeners are passive and could not stop Plotly's zoom.
  useEffect(() => {
    const wrapper = wrapperRef.current;

    if (!wrapper) {
      return;
    }

    const handleWheel = (event: WheelEvent) => handleWheelRef.current(event);
    wrapper.addEventListener("wheel", handleWheel, { capture: true, passive: false });

    return () => {
      wrapper.removeEventListener("wheel", handleWheel, { capture: true });

      if (wheelRestTimerRef.current !== null) {
        window.clearTimeout(wheelRestTimerRef.current);
      }
    };
  }, []);

  // Escape finishes adjusting.
  useEffect(() => {
    if (!adjustingSignal || !onAdjustEnd) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onAdjustEnd();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [adjustingSignal, onAdjustEnd]);

  // Double-click a point to remove it, or the plot to add a point there.
  const handleDoubleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const series = editingSeriesRef.current;
    const position = getPlotPosition(event);

    if (!series || !onInputChange || !position?.isInside || isOnOverlay(event.target)) {
      return;
    }

    const pointIndex = findPointAt(event);

    onInputChange(pointIndex >= 0
      ? removeInputPoint(series, pointIndex)
      : insertInputPoint(series, position.time, position.value));
  };

  // Plot area of both subplots relative to the wrapper and in the viewport,
  // with the x range currently shown. The range is read live, so it follows
  // a pan or wheel zoom while it is still in progress.
  const getTimeAxisGeometry = useCallback(() => {
    const wrapper = wrapperRef.current;
    const graphDiv = graphDivRef.current as (HTMLElement & { _fullLayout?: any }) | null;
    const plotSize = graphDiv?._fullLayout?._size;
    const range = graphDiv?._fullLayout?.xaxis?.range as [number, number] | undefined;

    if (!wrapper || !graphDiv || !plotSize || !range) {
      return null;
    }

    const wrapperRect = wrapper.getBoundingClientRect();
    const graphRect = graphDiv.getBoundingClientRect();

    return {
      left: graphRect.left - wrapperRect.left + plotSize.l,
      top: graphRect.top - wrapperRect.top + plotSize.t,
      clientLeft: graphRect.left + plotSize.l,
      clientTop: graphRect.top + plotSize.t,
      width: plotSize.w as number,
      height: plotSize.h as number,
      range,
    };
  }, []);

  // Time (s) under a viewport x position, clamped to the shown range.
  const clientXToTime = (clientX: number): number | null => {
    const geometry = getTimeAxisGeometry();

    if (!geometry) {
      return null;
    }

    const fraction = Math.min(Math.max((clientX - geometry.clientLeft) / geometry.width, 0), 1);
    return geometry.range[0] + fraction * (geometry.range[1] - geometry.range[0]);
  };

  // Move the marker lines to their times. Done on the DOM, so it can follow
  // pans and zooms without re-rendering the plot.
  const positionMarkers = useCallback(() => {
    const geometry = getTimeAxisGeometry();

    MARKER_IDS.forEach((id) => {
      const element = markerElementsRef.current[id];
      const time = markersRef.current[id];

      if (!element) {
        return;
      }

      const x = geometry && time !== null
        ? (time - geometry.range[0]) / (geometry.range[1] - geometry.range[0]) * geometry.width
        : NaN;

      if (!geometry || !(x >= 0 && x <= geometry.width)) {
        element.style.display = "none";
        return;
      }

      element.style.display = "block";
      element.style.left = `${geometry.left + x}px`;
      element.style.top = `${geometry.top}px`;
      element.style.height = `${geometry.height}px`;
    });
  }, [getTimeAxisGeometry]);

  useLayoutEffect(positionMarkers, [markers, positionMarkers]);

  const isOnOverlay = (target: EventTarget | null) =>
    target instanceof Element && Boolean(target.closest(".cm-graph-marker, .cm-marker-readout"));

  // A click on the plot (not the end of a pan) places marker A, then B, then
  // moves the nearer one. While an input is edited, clicks edit its points.
  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const start = pointerDownRef.current;
    const geometry = getTimeAxisGeometry();

    if (editingSeriesRef.current || adjustingRef.current || !start || !geometry || isOnOverlay(event.target)
      || Math.hypot(event.clientX - start.x, event.clientY - start.y) > CLICK_SLOP) {
      return;
    }

    const px = event.clientX - geometry.clientLeft;
    const py = event.clientY - geometry.clientTop;

    if (px < 0 || px > geometry.width || py < 0 || py > geometry.height) {
      return;
    }

    const lastClick = lastClickRef.current;

    // The second click of a double-click resets the zoom; undo the first.
    if (lastClick && event.timeStamp - lastClick.time < DOUBLE_CLICK_DELAY) {
      lastClickRef.current = null;
      setMarkers(lastClick.previous);
      return;
    }

    const pointerTime = clientXToTime(event.clientX);
    // Markers stick to signal edges; Alt places them freely.
    const time = pointerTime !== null && !event.altKey ? snapToEdge(pointerTime) ?? pointerTime : pointerTime;

    if (time !== null) {
      lastClickRef.current = { time: event.timeStamp, previous: markersRef.current };
      setMarkers(placeMarker(markersRef.current, time));
    }
  };

  const handleMarkerPointerDown = (id: MarkerId) => (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    draggingMarkerRef.current = id;
  };

  // A dragged marker sticks to signal edges; Alt moves it freely.
  const handleMarkerPointerMove = (id: MarkerId) => (event: React.PointerEvent<HTMLDivElement>) => {
    const pointerTime = draggingMarkerRef.current === id ? clientXToTime(event.clientX) : null;

    if (pointerTime === null) {
      return;
    }

    const snapped = event.altKey ? null : snapToEdge(pointerTime);
    showSnapGuide(snapped);
    setMarkers((current) => ({ ...current, [id]: snapped ?? pointerTime }));
  };

  const handleMarkerPointerUp = () => {
    draggingMarkerRef.current = null;
    showSnapGuide(null);
  };

  const removeMarker = (id: MarkerId) => (event: React.MouseEvent) => {
    event.stopPropagation();
    setMarkers((current) => ({ ...current, [id]: null }));
  };

  // The values of the shown signals at the markers. A shifted or scaled
  // signal reads the original value of the point drawn under the marker, so
  // deltas stay in the signal's own units.
  const markerReadout = useMemo(() => {
    if (markers.a === null && markers.b === null) {
      return null;
    }

    const legendItems = getLegendItems(dataMs, previousDataMs);
    const styles = new Map(legendItems.map((item) => [item.name, item]));
    const order = new Map(legendItems.map((item, index) => [item.name, index]));
    // Each signal is followed by its value in the previous run, if any.
    const previousSeries = previousDataMs
      ? Array.from(getSignalSeries(previousDataMs)).map(([name, series]) => [previousTraceName(name), series, name] as const)
      : [];
    const rows = [...Array.from(getSignalSeries(dataMs)).map(([name, series]) => [name, series, name] as const), ...previousSeries]
      .sort(([, , a], [, , b]) => (order.get(a) ?? 0) - (order.get(b) ?? 0))
      .filter(([, series, name]) => traceVisibility[name] !== false || (series.label === editingInput && name.startsWith("In: ")))
      .map(([name, series, signalName]) => {
        const isEdited = name === signalName && name.startsWith("In: ") && series.label === editingInput && Boolean(onInputChange);
        const transform = isEdited ? undefined : transforms?.[signalName];
        const timeShift = transform?.timeShift ?? 0;
        const at = (time: number | null) => time === null ? null : valueAt(series, Number(((time - timeShift) * 1000).toPrecision(12)));
        const a = at(markers.a);
        const b = at(markers.b);

        return {
          name,
          note: transform && !isIdentityTransform(transform) ? describeTransform(transform) : null,
          color: styles.get(signalName)?.color,
          isPrevious: name !== signalName,
          a,
          b,
          delta: a !== null && b !== null ? b - a : null,
        };
      });

    return {
      rows,
      deltaTime: markers.a !== null && markers.b !== null ? markers.b - markers.a : null,
    };
  }, [dataMs, editingInput, markers, onInputChange, previousDataMs, traceVisibility, transforms]);

  // Track the shown time range so it survives re-renders, and keep the
  // markers on their times while the plot pans and zooms.
  const handleRelayout = (event: Readonly<Plotly.PlotRelayoutEvent>) => {
    const update = event as Record<string, unknown>;
    const range = update["xaxis.range"] as [number, number] | undefined;
    const start = update["xaxis.range[0]"];
    const end = update["xaxis.range[1]"];

    if (range) {
      setZoomRange([range[0], range[1]]);
    } else if (typeof start === "number" && typeof end === "number") {
      setZoomRange([start, end]);
    } else if (update["xaxis.autorange"]) {
      setZoomRange(null);
    }

    positionMarkers();
  };

  const xRange = frozenRanges?.x || zoomRange || undefined;
  // Ranges are pinned or autoranged explicitly: with uirevision, Plotly
  // would otherwise keep autorange off after a pinned range is released and
  // fall back to its default range.
  const layout = useMemo<Partial<Plotly.Layout>>(() => ({
    // title: "Simulation Data with Step Binary Signal",
    autosize: true, // Ensure full scaling inside parent
    dragmode: "pan", // Enable panning
    // Keep the user's pan and zoom when the figure is re-rendered.
    uirevision: "graph",
    paper_bgcolor: "#ffffff",
    plot_bgcolor: "#ffffff",
    font: { color: "#121f19", family: "Inter, system-ui, sans-serif" },
    margin: { t: 50, l: 50, r: 50, b: 40 },
    showlegend: false, // The legend lives in the parameters pane
    grid: { rows: 2, columns: 1, roworder: "top to bottom" },
    shapes: [...stepShapes, ...highlightShapes],
    annotations: stepAnnotations,
    xaxis: {
      title: "Time [s]",
      domain: [0, 1],
      ...(xRange ? { range: xRange, autorange: false } : { autorange: true }), // Sync X-axis zoom
      showticklabels: true, // Show X labels on the main graph
      gridcolor: GRID_COLOR,
      zerolinecolor: ZERO_LINE_COLOR,
      linecolor: ZERO_LINE_COLOR,
      tickfont: { color: AXIS_TEXT_COLOR },
    },
    yaxis: {
      title: "Simulation Value",
      domain: MAIN_PLOT_DOMAIN, // Assigns 80% of space to main graph
      ...(frozenRanges ? { range: frozenRanges.y, autorange: false } : { autorange: true }),
      gridcolor: GRID_COLOR,
      zerolinecolor: ZERO_LINE_COLOR,
      tickfont: { color: AXIS_TEXT_COLOR },
    },
    xaxis2: {
      title: "Time [s]",
      domain: [0, 1],
      anchor: "y2",
      ...(xRange ? { range: xRange, autorange: false } : { autorange: true }), // Ensure sync with main X-axis
      showgrid: false, // Hide grid for cleaner look
      showticklabels: false, // Hide X labels for the bottom graph
    },
    yaxis2: {
      title: "Binary Signal",
      domain: [0, 0.1], // Assigns 10% of space to the binary graph
      range: [-0.1, 1.1], // Keep within 0 and 1
      showticklabels: false, // Hide Y labels for clarity
      showgrid: false,
      zeroline: false,
      fixedrange: true, // **Prevents Y zooming**
    },
  }) as Partial<Plotly.Layout>, [frozenRanges, highlightShapes, stepAnnotations, stepShapes, xRange]);

  const isEditing = Boolean(editingSeries);
  const config = useMemo<Partial<Plotly.Config>>(() => ({
    responsive: true,
    // The wheel zooms around the pointer: over the plot both axes, over an
    // axis only that axis.
    scrollZoom: true,
    // Double-click adds and removes input points while editing instead of
    // resetting the zoom.
    doubleClick: isEditing ? false : "reset+autosize",
  }), [isEditing]);

  return (
    <div
      ref={wrapperRef}
      className={`cm-graph-wrapper${editingSeries ? " is-editing-input" : ""}${adjustingRef.current ? " is-adjusting-signal" : ""}`}
      onMouseMove={handleMouseMove}
      onMouseLeave={hideCursorLine}
      onMouseDownCapture={handleMouseDownCapture}
      onDoubleClick={handleDoubleClick}
      onClick={handleClick}
    >
      <div ref={snapGuideRef} className="cm-graph-snap-guide" />
      <div ref={cursorLineRef} className="cm-graph-cursor">
        <div ref={cursorLabelRef} className="cm-graph-cursor-label" />
      </div>
      {MARKER_IDS.map((id) => markers[id] !== null && (
        <div
          key={id}
          ref={(element) => { markerElementsRef.current[id] = element; }}
          className="cm-graph-marker"
          title="Drag to move (snaps to signal edges, Alt moves freely), double-click to remove"
          onPointerDown={handleMarkerPointerDown(id)}
          onPointerMove={handleMarkerPointerMove(id)}
          onPointerUp={handleMarkerPointerUp}
          onPointerCancel={handleMarkerPointerUp}
          onDoubleClick={removeMarker(id)}
        >
          <span className="cm-graph-marker-label">{id.toUpperCase()}</span>
        </div>
      ))}
      {markerReadout && (
        <div className="cm-marker-readout">
          <table>
            <thead>
              <tr>
                <th />
                <th>A</th>
                <th>B</th>
                <th>Δ (B − A)</th>
              </tr>
            </thead>
            <tbody>
              <tr className="is-time">
                <th>Time [s]</th>
                <td>{formatNumber(markers.a)}</td>
                <td>{formatNumber(markers.b)}</td>
                <td>{formatNumber(markerReadout.deltaTime)}</td>
              </tr>
              {markerReadout.rows.map((row) => (
                <tr key={row.name}>
                  <th>
                    <span className={`cm-marker-readout-swatch${row.isPrevious ? " is-previous" : ""}`} style={{ background: row.color }} />
                    {row.name}
                    {row.note && <span className="cm-marker-readout-note">{row.note}</span>}
                  </th>
                  <td>{formatNumber(row.a)}</td>
                  <td>{formatNumber(row.b)}</td>
                  <td>{formatNumber(row.delta)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <Button
            small
            minimal
            icon={IconNames.Cross}
            className="cm-marker-readout-clear"
            title="Remove the markers"
            aria-label="Remove the markers"
            onClick={() => setMarkers(NO_MARKERS)}
          />
        </div>
      )}
      <Plot
      // data={[
      //   // Simulation results (line graph)
      //   {
      //     x: simulationData.x,
      //     y: simulationData.y.map((v) => v * 10), // Adjust for visibility on the same Y scale
      //     type: "scatter",
      //     mode: "lines",
      //     line: { color: "blue", width: 2 },
      //     name: "Simulation Data",
      //     xaxis: "x",
      //     yaxis: "y",
      //   },
      //   // Binary step-like signal (filled)
      //   {
      //     x: binaryStepData.stepX,
      //     y: binaryStepData.stepY,
      //     showlegend: false, // Hide from legend
      //     type: "scatter",
      //     mode: "lines",
      //     fill: "tozeroy",
      //     line: { shape: "vh", width: 0, color: "rgba(0, 255, 0, 1)" }, // "hv" makes it step-like
      //     fillcolor: "rgba(0, 255, 0, 0.5)", // Green fill for active
      //     name: "Success",
      //     xaxis: "x",
      //     yaxis: "y2",
      //     hoverinfo: "none", // Hide hover info for binary signal
      //   },
      //   {
      //     x: binaryStepData.stepX,
      //     y: invertedBinary,
      //     showlegend: false,
      //     type: "scatter",
      //     mode: "lines",
      //     fill: "tozeroy",
      //     line: { shape: "hv", width: 0, color: "rgba(0, 255, 0, 1)" }, // "hv" makes it step-like
      //     fillcolor: "rgba(255, 25, 0, 0.5)", // Green fill for active
      //     name: "Failure",
      //     xaxis: "x",
      //     yaxis: "y2",
      //     hoverinfo: "none", // Hide hover info for binary signal
      //   },
      //   // Step number annotations
      //   {
      //     x: binaryStepData.stepTextX,
      //     y: new Array(binaryStepData.stepTextX.length).fill(0.5), // Position slightly above graph
      //     text: binaryStepData.stepText,
      //     mode: "text",
      //     textposition: "middle center",
      //     showlegend: false,
      //     xaxis: "x",
      //     yaxis: "y2",
      //     hoverinfo: "none", // Hide hover info for step numbers
      //   },
      // ]}
      data={plotData}
      layout={layout}
        onInitialized={(_, graphDiv) => {
          graphDivRef.current = graphDiv;
          // Follow pans and wheel zooms while they happen. Missing from the
          // react-plotly.js props.
          (graphDiv as Plotly.PlotlyHTMLElement).on("plotly_relayouting", positionMarkers);
          positionMarkers();
        }}
        onUpdate={(_, graphDiv) => {
          graphDivRef.current = graphDiv;
          positionMarkers();
        }}
        onPurge={() => {
          graphDivRef.current = null;
        }}
        onRelayout={handleRelayout}
        config={config}
        style={{ width: "100%", height: "100%", flexGrow: 1 }}
        useResizeHandler={true}
      />
    </div>
  );
};

export default SimulationGraph;
